import {css} from 'aphrodite';
import React, {ReactElement} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import navigateToURL from 'lib/navigateToURL';
import * as sequtil from 'molbio/SeqUtil';
import {
  RelevantItemsFilteredTableState,
  RelevantItemsFilterOption,
} from 'relevant-items/PresentationalRelevantItemsSection';
import {
  getHeaderColumn,
  HEADER_COLUMNS,
  HeaderInfo,
  NOT_SORTABLE,
} from 'relevant-items/RelevantItemsHeaderColumns';
import BenchChip from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {Cell, CellProps, Column, simpleTableStyles, Table} from 'ui-library/simple-table/SimpleTable';
import SortableHeaderCell from 'ui-library/simple-table/SortableHeaderCell';
import useSimpleTableSortState, {SimpleTableSortState} from 'ui-library/simple-table/useSimpleTableSortState';
import APIUtil from 'util/APIUtil';
import * as DateUtil from 'util/DateUtil';
import filterItemsToSearchQuery from 'util/filterItemsToSearchQuery';
import sortedBy from 'util/sortedBy';
import * as WorkflowActions from 'workflows/WorkflowActions';

import {
  RelevantItemsGraphQL,
  RelevantItemsGraphQL_RelevantItemsNode_tagged as TaggedItem,
  RelevantItemsGraphQL_RelevantItemsNode_filesMentioning as FilesMentioningItem,
  RelevantItemsGraphQL_RelevantItemsNode_sequencesUsingAsPrimer_SequenceUsingAsPrimerNode as SequenceUsingAsPrimer,
  RelevantItemsGraphQL_RelevantItemsNode_workflows_WorkflowRelevantItemNode as RelevantWorkflowGQL,
} from './__generated/RelevantItemsGraphQL';
import getRelevantWorkflowInfo from './getRelevantWorkflowInfo';
import {RelevantProcess} from './RelevantItemsConstants';

function getLocation(item: {id: string; folder?: {source?: {name: string}}}): string | null {
  return item.folder?.source?.name ?? null;
}

interface RelevantRowData {
  id: string;
  name: string;
  referencedInText: string | JSX.Element;
  modifiedAt: string | null;
  location: string | null;
  chip: JSX.Element;
  onClick: () => void;
}

type SupportedRelevantItem<T> = Exclude<T, {__typename: 'EntryLikeDocumentNode'}>;

function getBaseRelevantItemRowData(
  itemsToShow: RelevantItemsFilterOption,
  areConvertedRelevantItems: boolean,
  relevantItemsGraphQL: RelevantItemsGraphQL | null
): Array<RelevantRowData> {
  const {
    bulkAssembliesCreatingAsAmplifiedFragment,
    bulkAssembliesCreatingAsConstruct,
    bulkAssembliesUsingAsFragment,
    filesMentioning,
    sequencesUsingAsPrimer,
    tagged,
    workflows,
  } =
    relevantItemsGraphQL != null
      ? relevantItemsGraphQL
      : {
          bulkAssembliesCreatingAsAmplifiedFragment: [],
          bulkAssembliesCreatingAsConstruct: [],
          bulkAssembliesUsingAsFragment: [],
          filesMentioning: [] as Array<FilesMentioningItem>,
          sequencesUsingAsPrimer: [],
          tagged: [] as Array<TaggedItem>,
          workflows: [],
        };

  function filterItems<T extends {id: string}>(items: Array<T>): Array<T> {
    switch (itemsToShow) {
      case RelevantItemsFilterOption.ALL:
        return items;
      case RelevantItemsFilterOption.BULK_ASSEMBLIES:
        return items.filter((f) => f.id && APIUtil.isBulkAssembly(f.id));
      case RelevantItemsFilterOption.ENTRIES:
        return items.filter((f) => f.id && APIUtil.isEntry(f.id));
      case RelevantItemsFilterOption.ENTITY_LINKS:
        return items.filter((f) => f.id && APIUtil.isBiologicalEntity(f.id));
      case RelevantItemsFilterOption.LEGACY_WORKFLOWS:
        return [];
      case RelevantItemsFilterOption.SEQUENCE_PRIMERS:
        return [];
      case RelevantItemsFilterOption.PROCESSES:
        return [];
      default:
        throw new Error('Invalid relevant items filter option');
    }
  }

  const filterSequences = (items: Array<SequenceUsingAsPrimer>): Array<SequenceUsingAsPrimer> =>
    [RelevantItemsFilterOption.ALL, RelevantItemsFilterOption.SEQUENCE_PRIMERS].includes(itemsToShow)
      ? items
      : [];

  const filterWorkflows = (items: Array<RelevantWorkflowGQL>): Array<RelevantWorkflowGQL> =>
    [RelevantItemsFilterOption.ALL, RelevantItemsFilterOption.LEGACY_WORKFLOWS].includes(itemsToShow)
      ? items
      : [];

  const filterTaskGroups = (items: Array<RelevantProcess>): Array<RelevantProcess> =>
    [RelevantItemsFilterOption.ALL, RelevantItemsFilterOption.PROCESSES].includes(itemsToShow) ? items : [];

  const entityLabel = areConvertedRelevantItems ? 'Converted item' : 'Entity';

  // If ELN_ENABLE_DOCUMENT_NOTE_REFERENCES flag is disabled, UI support hasn't been added yet for document note references.
  // Filter them out until the flag is enabled to display them properly.
  const supportedTagged = BenchlingConfig.ELN_ENABLE_DOCUMENT_NOTE_REFERENCES
    ? tagged
    : tagged.filter(
        (item): item is SupportedRelevantItem<typeof item> => item.__typename !== 'EntryLikeDocumentNode'
      );
  const supportedFilesMentioning = BenchlingConfig.ELN_ENABLE_DOCUMENT_NOTE_REFERENCES
    ? filesMentioning
    : filesMentioning.filter(
        (item): item is SupportedRelevantItem<typeof item> => item.__typename !== 'EntryLikeDocumentNode'
      );

  const taggedRows = filterItems(supportedTagged).map(
    (item): RelevantRowData => ({
      id: item.id,
      name: item.name,
      referencedInText: `${entityLabel} referenced in field`,
      modifiedAt: item.modifiedAt,
      location: getLocation(item),
      chip: <BenchChip id={item.id} size={ChipSize.SMALL} />,
      onClick: () => {
        FileBrowserActions.openFile(item.id);
      },
    })
  );

  const sequencesUsingAsPrimerRows = filterSequences(sequencesUsingAsPrimer).map(
    (item): RelevantRowData => ({
      id: item.sequence.id,
      name: item.sequence.name,
      referencedInText: `${entityLabel} bound as primer at position: ${
        item.bindStrand === 1 ? `+` : `-`
      }/${sequtil.modelToDisplay(item.bindPosition, item.sequence.startIndex)}`,
      modifiedAt: item.sequence.modifiedAt,
      location: getLocation(item.sequence),
      chip: <BenchChip id={item.sequence.id} size={ChipSize.SMALL} />,
      onClick: () => {
        FileBrowserActions.openFile(item.sequence.id);
      },
    })
  );

  const bulkAssembliesUsingAsFragmentRows = filterItems(bulkAssembliesUsingAsFragment).map((item) => ({
    id: item.id,
    name: item.name,
    referencedInText: `${entityLabel} used as fragment in assembly`,
    modifiedAt: item.modifiedAt,
    location: getLocation(item),
    chip: <BenchChip id={item.id} size={ChipSize.SMALL} />,
    onClick: () => {
      FileBrowserActions.openFile(item.id);
    },
  }));

  const bulkAssembliesCreatingAsConstructRows = filterItems(bulkAssembliesCreatingAsConstruct).map(
    (item) => ({
      id: item.id,
      name: item.name,
      referencedInText: `${entityLabel} created by assembly`,
      modifiedAt: item.modifiedAt,
      location: getLocation(item),
      chip: <BenchChip id={item.id} size={ChipSize.SMALL} />,
      onClick: () => {
        FileBrowserActions.openFile(item.id);
      },
    })
  );

  const bulkAssembliesCreatingAsAmplifiedFragmentRows = filterItems(
    bulkAssembliesCreatingAsAmplifiedFragment
  ).map((item) => ({
    id: item.id,
    name: item.name,
    referencedInText: `${entityLabel} created by assembly`,
    modifiedAt: item.modifiedAt,
    location: getLocation(item),
    chip: <BenchChip id={item.id} size={ChipSize.SMALL} />,
    onClick: () => {
      FileBrowserActions.openFile(item.id);
    },
  }));

  const filesMentioningRows = filterItems(supportedFilesMentioning).map((item) => ({
    id: item.id,
    name: item.name,
    referencedInText: APIUtil.isEntry(item.id)
      ? `${entityLabel} referenced in entry`
      : `${entityLabel} referenced in description`,
    modifiedAt: item.modifiedAt,
    location: getLocation(item),
    chip: <BenchChip id={item.id} size={ChipSize.SMALL} />,
    onClick: () => {
      FileBrowserActions.openFile(item.id);
    },
  }));

  const workflowsRows = filterWorkflows(workflows).map((item): RelevantRowData => {
    const {workflow} = item;

    const {description, modifiedAt, name, projectName} = getRelevantWorkflowInfo(
      item,
      areConvertedRelevantItems
    );

    return {
      id: workflow.id,
      name,
      referencedInText: description,
      modifiedAt,
      location: projectName,
      chip: (
        <Chip
          size={ChipSize.SMALL}
          text={name}
          icon="workflow"
          className="chip-container"
          onClick={() => {
            WorkflowActions.routeToWorkflow(item.workflow.id);
          }}
        />
      ),
      onClick: () => {
        WorkflowActions.routeToWorkflow(item.workflow.id);
      },
    };
  });

  const taskGroupsRows = relevantItemsGraphQL
    ? filterTaskGroups([...relevantItemsGraphQL.processOutputs, ...relevantItemsGraphQL.processTasks]).map(
        (item: RelevantProcess): RelevantRowData => {
          const {name, process} = item;
          const {modifiedAt, createdAt, editUrl, folder} = process;
          return {
            name,
            id: name,
            referencedInText: `${
              areConvertedRelevantItems ? 'Converted item' : 'Entity'
            } referenced in task group`,
            modifiedAt: modifiedAt ?? createdAt,
            location: folder?.name ?? null,
            chip: (
              <Chip
                text={name}
                icon="process"
                size={ChipSize.SMALL}
                className="chip-container"
                onClick={() => {
                  navigateToURL(editUrl ?? '');
                  analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_RELEVANT_ITEM, {
                    process_client_origin: item.process.processTaskSchema.clientOrigin,
                  });
                }}
              />
            ),
            onClick: () => {
              navigateToURL(editUrl ?? '');
            },
          };
        }
      )
    : [];

  const allRows = taggedRows.concat(
    sequencesUsingAsPrimerRows,
    bulkAssembliesUsingAsFragmentRows,
    bulkAssembliesCreatingAsConstructRows,
    bulkAssembliesCreatingAsAmplifiedFragmentRows,
    filesMentioningRows,
    workflowsRows,
    taskGroupsRows
  );
  return allRows;
}

function getRelevantItemRowData(props: {
  relevantItemsGraphQL: RelevantItemsGraphQL;
  relevantConvertedFromItemsGraphQL: RelevantItemsGraphQL | null;
  filteredTableState: RelevantItemsFilteredTableState;
}): Array<RelevantRowData> {
  const itemsToShow = props.filteredTableState.filterOption;
  const baseRelevantItemRows = getBaseRelevantItemRowData(itemsToShow, false, props.relevantItemsGraphQL);
  const convertedFromRelevantItemRows = getBaseRelevantItemRowData(
    itemsToShow,
    true,
    props.relevantConvertedFromItemsGraphQL
  );

  const rows = [...baseRelevantItemRows, ...convertedFromRelevantItemRows];
  return rows;
}

function lowercaseIncludes(text: string, includesString: string): boolean {
  return text.toLowerCase().includes(includesString.toLowerCase());
}

function matchName(relevantItem: RelevantRowData, searchQuery: string): boolean {
  return lowercaseIncludes(relevantItem.name, searchQuery);
}

function matchModifiedAt(relevantItem: RelevantRowData, searchQuery: string): boolean {
  return (
    relevantItem.modifiedAt != null &&
    lowercaseIncludes(DateUtil.readableTime(relevantItem.modifiedAt), searchQuery)
  );
}

function matchLocation(relevantItem: RelevantRowData, searchQuery: string): boolean {
  return relevantItem.location != null && lowercaseIncludes(relevantItem.location, searchQuery);
}

function getSortedRelevantItemRowData(
  relevantItems: Array<RelevantRowData>,
  sortState: Exclude<SimpleTableSortState, null>
): Array<RelevantRowData> {
  let sortedItems = relevantItems;
  const headerColumn = getHeaderColumn(sortState.columnKey as number);
  if (headerColumn) {
    sortedItems = sortedBy(relevantItems, [
      {
        accessor: headerColumn.propertyKey,
        naturalSort: true,
        reversed: sortState.sortDirection === 'arrow-up',
      },
    ]);
  }

  return sortedItems;
}

export interface RelevantItemsSectionTableProps {
  relevantItemsGraphQL: RelevantItemsGraphQL;
  relevantConvertedFromItemsGraphQL: RelevantItemsGraphQL | null;
  filteredTableState: RelevantItemsFilteredTableState;
  setFilteredTableState: (state: RelevantItemsFilteredTableState) => void;
  itemId: string;
  /** @default false */
  isCompact?: boolean;
  /** @default 100 */
  paginationLimit?: number;
}

/**
 * Table part of the relevant items section.
 *
 * @package This is meant for internal use, and should not generally be directly rendered; rather, use either
 * PresentationalRelevantItemsSection or RelevantItemsSection.
 *
 */
export default function RelevantItemsSectionTable(props: RelevantItemsSectionTableProps): JSX.Element {
  const {pageOffset, searchQuery, filterOption} = props.filteredTableState;

  const allRowData = getRelevantItemRowData(props);
  const filteredRowData = filterItemsToSearchQuery(allRowData, searchQuery, [
    matchName,
    matchModifiedAt,
    matchLocation,
  ]);
  const {sortState, getHeaderCellProps} = useSimpleTableSortState(props.itemId);
  const rowData = getSortedRelevantItemRowData(
    filteredRowData,
    sortState ?? {
      columnKey: HEADER_COLUMNS.modifiedAt.sortIndex,
      sortDirection: 'arrow-up',
    }
  );

  function renderHeader(headerInfo: HeaderInfo): ReactElement<CellProps> {
    assert(headerInfo.sortIndex !== NOT_SORTABLE, 'Expected header to have sortable index');

    return (
      <SortableHeaderCell {...getHeaderCellProps(headerInfo.sortIndex)}>{headerInfo.name}</SortableHeaderCell>
    );
  }

  function setOffset(pageOffset: number): void {
    props.setFilteredTableState({
      filterOption,
      searchQuery,
      pageOffset,
    });
  }

  const paginationLimit = props.paginationLimit ?? 100;

  return (
    <Table
      emptyTableText="No relevant items were found."
      numRows={Math.min(rowData.length - pageOffset, paginationLimit)}
      pagination={{offset: pageOffset, limit: paginationLimit, total: rowData.length}}
      setOffset={setOffset}
      isCompact={props.isCompact}
    >
      <Column
        header={renderHeader(HEADER_COLUMNS.item)}
        renderCell={(rowIndex) => <Cell>{rowData[pageOffset + rowIndex].chip}</Cell>}
      />
      <Column
        header={
          <Cell className={css(simpleTableStyles.grayHoverHeaderCell)}>{HEADER_COLUMNS.reference.name}</Cell>
        }
        renderCell={(rowIndex) => <Cell>{rowData[pageOffset + rowIndex].referencedInText}</Cell>}
      />
      <Column
        header={renderHeader(HEADER_COLUMNS.modifiedAt)}
        renderCell={(rowIndex) => (
          <Cell>
            {rowData[pageOffset + rowIndex].modifiedAt &&
              DateUtil.readableTime(rowData[pageOffset + rowIndex].modifiedAt)}
          </Cell>
        )}
      />
      <Column
        header={renderHeader(HEADER_COLUMNS.projectLocation)}
        renderCell={(rowIndex) => <Cell>{rowData[pageOffset + rowIndex].location}</Cell>}
      />
    </Table>
  );
}
